import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
}

export function Tabs({ tabs, activeTab, onChange, className }: TabsProps) {
  const scrollRef = useRef<HTMLElement>(null);
  // Indiciu vizual: afișăm un gradient discret în dreapta cât timp mai există
  // taburi ascunse la care se poate derula (utile pe laptop/tabletă).
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateHint = () => {
      // 1px toleranță pentru rotunjiri de subpixel.
      const hasMore =
        el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
      setShowScrollHint(hasMore);
    };

    updateHint();
    el.addEventListener('scroll', updateHint, { passive: true });
    window.addEventListener('resize', updateHint);
    return () => {
      el.removeEventListener('scroll', updateHint);
      window.removeEventListener('resize', updateHint);
    };
  }, [tabs]);

  return (
    <div className={clsx('relative border-b border-gray-200', className)}>
      <nav
        ref={scrollRef}
        className="-mb-px flex space-x-6 overflow-x-auto whitespace-nowrap"
        aria-label="Tabs"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={clsx(
              'group inline-flex flex-shrink-0 items-center py-3 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            {tab.icon && (
              <span
                className={clsx(
                  'mr-2',
                  activeTab === tab.id
                    ? 'text-primary-500'
                    : 'text-gray-400 group-hover:text-gray-500'
                )}
              >
                {tab.icon}
              </span>
            )}
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className={clsx(
                  'ml-2 py-0.5 px-2 rounded-full text-xs font-medium',
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-600'
                )}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
      {/* Indiciu discret că mai există taburi în dreapta (fără interacțiune). */}
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent transition-opacity duration-200',
          showScrollHint ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
}

interface TabPanelProps {
  children: React.ReactNode;
  tabId: string;
  activeTab: string;
  className?: string;
}

export function TabPanel({ children, tabId, activeTab, className }: TabPanelProps) {
  if (tabId !== activeTab) return null;

  return <div className={clsx('pt-4', className)}>{children}</div>;
}

// Componenta combinată pentru ușurință în utilizare
interface TabsContainerProps {
  tabs: Tab[];
  defaultTab?: string;
  children: (activeTab: string) => React.ReactNode;
  className?: string;
}

export function TabsContainer({
  tabs,
  defaultTab,
  children,
  className,
}: TabsContainerProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');

  return (
    <div className={className}>
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      <div className="pt-4">{children(activeTab)}</div>
    </div>
  );
}
